import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  History, 
  ArrowRightLeft, 
  CheckCircle2, 
  XCircle, 
  Clock,
  Mail,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  MailX,
  X
} from 'lucide-react';
import { formatDateTime } from '../../lib/utils';
import { requestsApi } from '../../lib/api';

export function AuditTab({ 
  request, 
  auditLogs, 
  handoverHistory 
}) {
  const [isHandoverTimelineExpanded, setIsHandoverTimelineExpanded] = useState(false);
  // iter127 — Email Notifications collapsible section. Lazy-loads on first expand
  // and refetches whenever the user re-expands it.
  const [emailLogOpen, setEmailLogOpen] = useState(false);
  const [emailLog, setEmailLog] = useState([]);
  const [emailLogLoading, setEmailLogLoading] = useState(false);
  const [emailLogLoaded, setEmailLogLoaded] = useState(false);
  const toggleEmailLog = async () => {
    const next = !emailLogOpen;
    setEmailLogOpen(next);
    if (next) {
      setEmailLogLoading(true);
      try {
        const res = await requestsApi.getEmailLog(request.id);
        setEmailLog(res.data || []);
        setEmailLogLoaded(true);
      } catch (e) {
        setEmailLog([]);
      } finally {
        setEmailLogLoading(false);
      }
    }
  };
  const humanizeTemplateKey = (k) => {
    if (!k) return 'Email';
    return k.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  };

  return (
    <div className="space-y-6">
      {/* General Audit Log */}
      <Card>
        <CardHeader>
          <CardTitle>Audit Log</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* iter148f — hide `generate_download_url` audit rows: they're internal
                plumbing for signed-URL minting; the `download_document` row tells the
                story. */}
            {auditLogs.filter(l => l.action_key !== 'generate_download_url').map((log) => {
              const isAdminOverride = log.action_key === 'admin_status_override';
              return (
              <div key={log.id} className={`timeline-item ${isAdminOverride ? 'border-l-2 border-red-400 pl-2 -ml-2' : ''}`} data-testid={`audit-${log.id}`}>
                <div className={`timeline-dot ${isAdminOverride ? '!bg-red-500' : ''}`}>
                  <History className="w-3 h-3 text-white" />
                </div>
                <div>
                  <p className={`font-medium ${isAdminOverride ? 'text-red-700' : 'text-slate-900'}`}>
                    {isAdminOverride ? 'Admin Status Override' : log.action_key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </p>
                  {isAdminOverride && log.payload && (
                    <div className="mt-1 p-2 bg-red-50 rounded border border-red-200 text-xs space-y-1">
                      <p className="text-red-700">
                        <span className="font-medium">{log.payload.from_status}</span> → <span className="font-medium">{log.payload.to_status}</span>
                      </p>
                      <p className="text-red-600">Reason: {log.payload.reason}</p>
                    </div>
                  )}
                  <p className="text-sm text-slate-500">
                    {log.action_key === 'request_executed' ? (
                      <>{formatDateTime(log.created_at)}</>
                    ) : (
                      <>By {log.actor_name}{isAdminOverride ? ' (Admin Override)' : ''} • {formatDateTime(log.created_at)}</>
                    )}
                  </p>
                </div>
              </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Handover Timeline - only show for CTA requests with handover history */}
      {request.type === 'CTA' && handoverHistory.length > 0 && (
        <HandoverTimeline 
          handoverHistory={handoverHistory}
          isExpanded={isHandoverTimelineExpanded}
          setIsExpanded={setIsHandoverTimelineExpanded}
        />
      )}

      {/* iter127 — Email Notifications collapsible section (every email sent for this request) */}
      <EmailNotificationsCard
        emailLogOpen={emailLogOpen}
        toggleEmailLog={toggleEmailLog}
        emailLogLoaded={emailLogLoaded}
        emailLog={emailLog}
        emailLogLoading={emailLogLoading}
        humanizeTemplateKey={humanizeTemplateKey}
      />
    </div>
  );
}

function HandoverTimeline({ handoverHistory, isExpanded, setIsExpanded }) {
  const statusColors = {
    'PENDING': 'bg-yellow-500',
    'ACCEPTED': 'bg-green-500',
    'REJECTED': 'bg-red-500'
  };
  
  const statusLabels = {
    'PENDING': 'Pending',
    'ACCEPTED': 'Accepted',
    'REJECTED': 'Rejected'
  };

  return (
    <Card className="border-amber-200 bg-amber-50/30">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <ArrowRightLeft className="w-5 h-5 text-amber-600" />
            Coordinator Handover Timeline
            <Badge variant="outline" className="ml-2 text-xs">
              {handoverHistory.length} handover{handoverHistory.length > 1 ? 's' : ''}
            </Badge>
          </CardTitle>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-amber-700 hover:text-amber-800 hover:bg-amber-100"
            data-testid="toggle-handover-timeline"
          >
            {isExpanded ? (
              <>
                <X className="w-4 h-4 mr-1" />
                Collapse
              </>
            ) : (
              <>
                <History className="w-4 h-4 mr-1" />
                Expand
              </>
            )}
          </Button>
        </div>
      </CardHeader>
      {isExpanded && (
        <CardContent>
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-amber-300" />
            
            <div className="space-y-4">
              {handoverHistory.map((handover, index) => (
                <div key={handover.id} className="relative pl-10" data-testid={`handover-timeline-${index}`}>
                  {/* Timeline dot */}
                  <div className={`absolute left-2 w-5 h-5 rounded-full ${statusColors[handover.status]} border-2 border-white shadow flex items-center justify-center`}>
                    {handover.status === 'ACCEPTED' && <CheckCircle2 className="w-3 h-3 text-white" />}
                    {handover.status === 'REJECTED' && <XCircle className="w-3 h-3 text-white" />}
                    {handover.status === 'PENDING' && <Clock className="w-3 h-3 text-white" />}
                  </div>
                  
                  <div className="bg-white rounded-lg p-3 border border-slate-200 shadow-sm">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-slate-800">
                        {handover.from_coordinator_name}
                      </span>
                      <ArrowRightLeft className="w-4 h-4 text-slate-400" />
                      <span className="font-semibold text-slate-800">
                        {handover.to_coordinator_name}
                      </span>
                      <Badge className={`ml-auto ${
                        handover.status === 'ACCEPTED' ? 'bg-green-100 text-green-700' :
                        handover.status === 'REJECTED' ? 'bg-red-100 text-red-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {statusLabels[handover.status]}
                      </Badge>
                    </div>
                    
                    {handover.reason && (
                      <p className="text-sm text-slate-600 mt-1 italic">
                        "{handover.reason}"
                      </p>
                    )}
                    
                    <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                      <span>Requested: {formatDateTime(handover.created_at)}</span>
                      {handover.responded_at && (
                        <span>
                          {handover.status === 'ACCEPTED' ? 'Accepted' : 'Rejected'}: {formatDateTime(handover.responded_at)}
                        </span>
                      )}
                    </div>
                    
                    {handover.rejection_reason && (
                      <div className="mt-2 p-2 bg-red-50 rounded text-sm text-red-700">
                        <strong>Rejection reason:</strong> {handover.rejection_reason}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
}

function EmailNotificationsCard({ emailLogOpen, toggleEmailLog, emailLogLoaded, emailLog, emailLogLoading, humanizeTemplateKey }) {
  return (
    <Card data-testid="email-notifications-card">
      <CardHeader className="cursor-pointer select-none" onClick={toggleEmailLog} data-testid="email-notifications-toggle">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-slate-600" />
            Email Notifications
            {emailLogLoaded && (
              <span className="text-xs font-normal text-slate-500 ml-1">({emailLog.length})</span>
            )}
          </CardTitle>
          {emailLogOpen ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
        </div>
      </CardHeader>
      {emailLogOpen && (
        <CardContent>
          {emailLogLoading ? (
            <div className="text-sm text-slate-500 py-4 text-center">Loading email log…</div>
          ) : emailLog.length === 0 ? (
            <div className="text-sm text-slate-500 py-4 text-center">No emails have been sent for this request yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                    <th className="py-2 pr-4">When</th>
                    <th className="py-2 pr-4">Type</th>
                    <th className="py-2 pr-4">To</th>
                    <th className="py-2 pr-4">Triggered by</th>
                    <th className="py-2 pr-4">Delivery</th>
                    <th className="py-2">Message</th>
                  </tr>
                </thead>
                <tbody>
                  {emailLog.map((e) => {
                    // iter128 — render real delivery outcome instead of pretending everything was sent.
                    // Rows from before iter128 don't have email_status — show 'Unknown' instead of faking success.
                    const status = e.email_status || 'unknown';
                    const deliveryUi = (() => {
                      switch (status) {
                        case 'sent':
                          return { label: 'Delivered', icon: <CheckCircle2 className="w-3 h-3" />, cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' };
                        case 'mock':
                          return { label: 'Mock (no SMTP)', icon: <AlertTriangle className="w-3 h-3" />, cls: 'bg-amber-100 text-amber-800 border-amber-200' };
                        case 'failed':
                          return { label: 'Failed', icon: <XCircle className="w-3 h-3" />, cls: 'bg-red-100 text-red-700 border-red-200' };
                        case 'skipped_no_addr':
                          return { label: 'No email on file', icon: <MailX className="w-3 h-3" />, cls: 'bg-slate-100 text-slate-600 border-slate-200' };
                        case 'skipped_disabled':
                          return { label: 'Email disabled', icon: <MailX className="w-3 h-3" />, cls: 'bg-slate-100 text-slate-600 border-slate-200' };
                        case 'skipped_no_vars':
                          return { label: 'In-app only', icon: <Mail className="w-3 h-3" />, cls: 'bg-blue-50 text-blue-700 border-blue-200' };
                        case 'unknown':
                          return { label: 'Unknown', icon: <AlertTriangle className="w-3 h-3" />, cls: 'bg-slate-100 text-slate-600 border-slate-200' };
                        default:
                          return { label: status, icon: <Mail className="w-3 h-3" />, cls: 'bg-slate-100 text-slate-600 border-slate-200' };
                      }
                    })();
                    return (
                      <tr
                        key={e.id}
                        className="border-b border-slate-100 last:border-b-0 align-top"
                        data-testid={`email-log-row-${e.id}`}
                      >
                        <td className="py-2 pr-4 whitespace-nowrap text-slate-500 text-xs">{formatDateTime(e.created_at)}</td>
                        <td className="py-2 pr-4">
                          <Badge className={e.automated ? 'bg-slate-100 text-slate-700' : 'bg-amber-100 text-amber-800'}>
                            {e.automated ? humanizeTemplateKey(e.template_key) : (e.kind === 'nudge' ? 'Nudge' : humanizeTemplateKey(e.template_key))}
                          </Badge>
                        </td>
                        <td className="py-2 pr-4">
                          <div className="text-slate-800">{e.to_user_name || e.to_email}</div>
                          {e.to_email && e.to_user_name && (
                            <div className="text-xs text-slate-400">{e.to_email}</div>
                          )}
                        </td>
                        <td className="py-2 pr-4 text-slate-700">
                          {e.automated ? (
                            <span className="text-xs text-slate-500">System</span>
                          ) : (
                            <span>{e.by_user_name || '—'}</span>
                          )}
                        </td>
                        <td className="py-2 pr-4" data-testid={`email-log-delivery-${e.id}`}>
                          <Badge
                            variant="outline"
                            className={`inline-flex items-center gap-1 border ${deliveryUi.cls}`}
                            title={e.email_error || deliveryUi.label}
                          >
                            {deliveryUi.icon}
                            <span className="text-[11px]">{deliveryUi.label}</span>
                          </Badge>
                          {e.email_error && status === 'failed' && (
                            <div className="text-[11px] text-red-600 mt-1 max-w-xs whitespace-normal">{e.email_error}</div>
                          )}
                        </td>
                        <td className="py-2 text-slate-700">{e.message}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}

export default AuditTab;
